import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { getServiceBySlug } from "../lib/data/services";
import { getCityBySlug, CITIES } from "../lib/data/cities";
import { SERVICES } from "../lib/data/services";
import { setSEO } from "../lib/data/seo";
import { Reveal, RevealLines } from "../components/motion/Reveal";
import SpotlightImage from "../components/site/SpotlightImage";
import EditorialMarquee from "../components/site/EditorialMarquee";
import ContactBlock from "../components/site/ContactBlock";

// SEO landing page: /:serviceSlug-:citySlug e.g. /mobile-patrol-vancouver
export default function ServiceCity() {
  const { combo } = useParams();

  // find first matching service slug prefix
  const match = SERVICES.map((s) => ({ s, prefix: `${s.slug}-` }))
    .find((m) => combo?.startsWith(m.prefix));

  const service = match?.s;
  const citySlug = match ? combo.slice(match.prefix.length) : null;
  const city = citySlug ? getCityBySlug(citySlug) : null;

  useEffect(() => {
    if (!service || !city) return;
    const title = `${service.name} in ${city.name}, BC — ${service.tagline}`;
    setSEO({
      title,
      description: `Licensed ${service.name.toLowerCase()} in ${city.name}, British Columbia. ${service.overview.slice(0, 140)}`,
      keywords: [
        `${service.name} ${city.name}`,
        `${service.name} ${city.name} BC`,
        `${service.slug.replace(/-/g, " ")} ${city.name}`,
        `security guards ${city.name}`,
        `${service.name} British Columbia`,
        ...(service.keywords || []),
        ...(city.keywords || []),
      ],
      canonical: `${window.location.origin}/${service.slug}-${city.slug}`,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Service",
        name: `${service.name} in ${city.name}, BC`,
        provider: {
          "@type": "SecurityService",
          name: "British Columbia Security Services",
          areaServed: `${city.name}, British Columbia`,
        },
        description: `${service.overview} Local delivery in ${city.name}, ${city.region}.`,
        areaServed: `${city.name}, BC`,
      },
    });
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [service, city]);

  if (!service || !city) return <Navigate to="/services" replace />;

  const otherCities = CITIES.filter((c) => c.slug !== city.slug).slice(0, 12);
  const otherServices = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 6);

  return (
    <div className="pt-24">
      {/* HERO */}
      <section className="pt-12 md:pt-20 pb-16 bg-[#F4F4F0]" data-testid="sc-hero">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="flex items-center gap-4 flex-wrap font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              <Link to={`/services/${service.slug}`}>← {service.name}</Link>
              <span className="text-black/30">|</span>
              <Link to={`/cities/${city.slug}`}>{city.name} →</Link>
            </div>
            <RevealLines
              lines={[`${service.name}`, `in ${city.name}, BC.`]}
              className="mt-6 font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9] text-[#060913]"
            />
            <p className="mt-8 max-w-xl text-lg md:text-xl text-[#5C5F66] leading-relaxed">
              {service.tagline} Local delivery in {city.name}, {city.region} —
              dispatched under our BC-wide operations desk with GPS-verified
              patrols, digital reporting, and licensed officers.
            </p>
          </div>
          <div className="md:col-span-5">
            <SpotlightImage
              src={service.image}
              alt={`${service.name} in ${city.name}, BC`}
              caption={`${service.number} · ${city.name}`}
              className="aspect-[4/5]"
            />
          </div>
        </div>
      </section>

      <EditorialMarquee items={[`${service.name.toUpperCase()} · ${city.name.toUpperCase()}`, "BRITISH COLUMBIA", "LICENSED · BONDED · INSURED"]} />

      {/* WHY HERE */}
      <section className="bg-[#F4F4F0] py-24 md:py-32" data-testid="sc-why">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              Why {city.name}
            </div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl leading-[0.95] tracking-tight text-[#060913]">
              Built for {city.name}'s specific risks.
            </h2>
          </div>
          <div className="md:col-span-8">
            <Reveal>
              <p className="text-lg md:text-xl text-[#060913] leading-relaxed">
                {service.overview}
              </p>
              <p className="mt-6 text-lg md:text-xl text-[#5C5F66] leading-relaxed">
                In {city.name}, our {service.name.toLowerCase()} teams work across
                {" "}{city.highlights ? city.highlights.slice(0, 2).join(", ").toLowerCase() : "residential and commercial properties"}
                {" "}and beyond — with the same standards we hold in every corner of BC.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SCOPE */}
      <section className="bg-white border-y border-black/10 py-24" data-testid="sc-scope">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
            {service.name} · {city.name}
          </div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl tracking-tight text-[#060913] max-w-3xl">
            What's included when we deliver {service.name.toLowerCase()} in {city.name}.
          </h2>
          <ul className="mt-10 border-t border-black/10 max-w-4xl">
            {service.scope.map((item, i) => (
              <li key={i} className="grid grid-cols-12 gap-4 py-5 border-b border-black/10">
                <span className="col-span-2 md:col-span-1 font-mono text-xs tracking-[0.2em] text-[#D92323]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="col-span-10 md:col-span-11 text-lg text-[#060913]">
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SEO KEYWORD CLUSTER */}
      <section className="bg-[#F4F4F0] py-20" data-testid="sc-keywords">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
            Also known as
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {[
              `${service.name} ${city.name}`,
              `${service.name} ${city.name} BC`,
              `${service.name} near ${city.name}`,
              `licensed ${service.name.toLowerCase()} ${city.name}`,
              `24 hour ${service.name.toLowerCase()} ${city.name}`,
              `${service.name} ${city.region}`,
              ...(service.keywords || []).slice(0, 4).map((k) => `${k} · ${city.name}`),
            ].map((k, i) => (
              <span key={i} className="font-mono text-[11px] tracking-[0.18em] uppercase border border-black/20 px-3 py-1.5 text-[#060913]/80">
                {k}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* INTERNAL LINKING */}
      <section className="bg-white py-24 border-y border-black/10" data-testid="sc-related">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-16">
          <div>
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              {service.name} across BC
            </div>
            <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-2">
              {otherCities.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/${service.slug}-${c.slug}`}
                    className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#060913]/70 hover:text-[#D92323]"
                  >
                    {service.name} · {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              Other services in {city.name}
            </div>
            <ul className="mt-6 grid grid-cols-1 gap-y-2">
              {otherServices.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/${s.slug}-${city.slug}`}
                    className="font-display text-2xl text-[#060913] hover:text-[#D92323]"
                  >
                    {s.name} in {city.name} →
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section className="bg-[#060913] text-white py-24 md:py-32" data-testid="sc-quote">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              Get a quote
            </div>
            <h2 className="mt-4 font-display text-4xl md:text-6xl tracking-tight leading-[0.95]">
              {service.name} in {city.name} — scoped in 24 hours.
            </h2>
          </div>
          <div className="md:col-span-6">
            <ContactBlock variant="dark" testid={`sc-${service.slug}-${city.slug}-contact`} />
          </div>
        </div>
      </section>
    </div>
  );
}
